import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { FormResultForEmployeeModal } from "@/components/form-result/result-modal/FormResultForEmployeeModal.tsx";
import { FormParticipantStatusTag } from "@/components/forms/components/FormParticipantStatusTag.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { Search } from "@/components/search/Search.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import { ReminderSendModal } from "@/components/reminder/ReminderSendModal.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ReminderEntityType } from "@/types/reminder.types.ts";
import {
  EmptyStateBase,
  PaginationBlock,
  useModals,
  ButtonLink,
  DropdownMenu,
  DropdownMenuItem,
  Stack,
  Table,
  Tag,
} from "@skailer-com/uikit";
import { useSurveyParticipantSearch } from "@/queries/surveys/use-surveys.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";

import { ApiFormParticipantStatus, ApiFormType } from "@/types/forms/form.types.ts";
import {
  ApiSurvey,
  ApiSurveyParticipant,
  ApiSurveyParticipantFilter,
  ApiSurveyStatus,
} from "@/types/survey.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export const SurveyParticipants = ({
  survey,
  filter,
  filterCount,
  sorting,
  onApplySorting,
}: {
  survey: ApiSurvey;
  filter: Omit<ApiSurveyParticipantFilter, "survey_id">;
  filterCount: number;
  sorting?: ApiSorting;
  onApplySorting: (sorting: ApiSorting) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") ?? "");

  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (value) => {
      setSearchParams((params) => {
        if (value) params.set("search", value);
        else params.delete("search");
        params.set("page", "1");
        return params;
      });
    },
  });
  const library = filtersLibrary();
  const {
    data: participants,
    isLoading,
    isFetching,
  } = useSurveyParticipantSearch({
    filter: { ...filter, survey_id: survey.id },
  });
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { renderTimeAgo } = useFormatter();

  const handleViewResults = useCallback(
    (employeeId: number) => {
      openModal(FormResultForEmployeeModal, {
        employeeId: employeeId,
        questionnaireId: survey.id,
        type: ApiFormType.survey,
      });

      analyticsService.trackEvent(analyticEvents.surveys.participantResultViewed, {
        [analyticProperties.id]: employeeId,
        [analyticProperties.source]: survey.id,
      });
    },
    [openModal, survey.id]
  );

  const columns = useMemo<ColumnDef<ApiSurveyParticipant>[]>(() => {
    const inner: ColumnDef<ApiSurveyParticipant>[] = [
      {
        id: "employee_name",
        enableSorting: true,
        sortDescFirst: false,
        header: t("core:employee"),
        cell: (row) => (
          <EmployeeHoverCard
            employee={row.row.original.employee}
            position={getEmployeePositionTitle(row.row.original.employee)}
          >
            <EmployeeItem showPosition={false} employee={row.row.original.employee} />
          </EmployeeHoverCard>
        ),
        accessorKey: "employee",
        ...ColumnSizes.employee,
      },
      {
        id: "status",
        enableSorting: survey.anonymous_threshold === 0,
        sortDescFirst: false,
        header: t("common:status"),
        cell: (row) => (
          <Stack items={"start"}>
            {row.row.original.status ? (
              <FormParticipantStatusTag status={row.row.original.status} />
            ) : (
              <Tag>{t("surveys:anonymous_participated")}</Tag>
            )}
          </Stack>
        ),
        accessorKey: "status",
        ...ColumnSizes.status,
      },
    ];

    return inner;
  }, [survey.anonymous_threshold, t]);

  const renderRowAction = useCallback(
    (row: Row<ApiSurveyParticipant>) => {
      const canView =
        survey.anonymous_threshold === 0 &&
        row.original.status == ApiFormParticipantStatus.completed;
      const canRemind = survey.status !== ApiSurveyStatus.completed;

      if (!canView && !canRemind) return null;

      return (
        <DropdownMenu>
          {canView && (
            <DropdownMenuItem
              onClick={() => handleViewResults(row.original.employee.id)}
              title={t("common:view")}
            />
          )}
          {canRemind && (
            <DropdownMenuItem
              onClick={() =>
                openModal(ReminderSendModal, {
                  recipients: [row.original.employee],
                  entityType: ReminderEntityType.survey,
                  entityIds: [survey.id],
                })
              }
              title={t("core:remind")}
              description={
                row.original.datetime_last_reminder
                  ? t("core:last_reminded", {
                      time: renderTimeAgo(row.original.datetime_last_reminder),
                    })
                  : ""
              }
            />
          )}
        </DropdownMenu>
      );
    },
    [
      survey.anonymous_threshold,
      survey.status,
      survey.id,
      handleViewResults,
      openModal,
      renderTimeAgo,
      t,
    ]
  );

  return (
    <>
      <Search
        value={searchTerm}
        onInput={(event) => setSearchTerm(event.currentTarget.value)}
        placeholder={t("common:search")}
        clearable
        onClear={() => setSearchTerm("")}
      />
      <Table
        sorting={sorting}
        onSortUpdated={onApplySorting}
        renderRowAction={renderRowAction}
        data={participants?.items || []}
        columns={columns}
        showSkeleton={isLoading}
        isFetching={isLoading || isFetching}
        showNotFoundError={filterCount > 0 && participants?.metadata.total_items === 0}
        notFoundComponent={<EmptyStateNothingFound />}
        emptyStateComponent={
          <EmptyStateBase
            emoji={":busts_in_silhouette:"}
            title={t("surveys:participants_empty_state_title")}
            description={t("surveys:participants_empty_state_description")}
            primaryButton={
              survey.acl.can_edit && (
                <ButtonLink variant={"secondary"} href={`/surveys/${survey.id}/setup`}>
                  {t("surveys:edit_survey")}
                </ButtonLink>
              )
            }
          />
        }
      />
      <PaginationBlock
        metadata={participants?.metadata}
        showOnSinglePage={true}
        onPageChange={(page) => {
          setSearchParams((searchParams) => {
            searchParams.set(library.page_number.searchParamName, page.toString());
            return searchParams;
          });
        }}
      />
    </>
  );
};
